import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { Building2, ShieldCheck, BarChart3, Users, ArrowRight, Loader2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { validatePassword, trimText } from '@/lib/passwordValidation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  { icon: Building2, title: 'Complete Loan Lifecycle', desc: 'Origination to closure, manage every stage of lending' },
  { icon: ShieldCheck, title: 'RBI Compliance Built-in', desc: 'NPA classification, IRAC provisioning, risk dashboards' },
  { icon: BarChart3, title: 'Real-time Analytics', desc: 'Portfolio health, collection efficiency, DPD distribution' },
  { icon: Users, title: 'Multi-user Access', desc: 'Role-based permissions for your entire team' },
];

const BLOCKED_DOMAINS = [
  'gmail.com', 'yahoo.com', 'yahoo.in', 'yahoo.co.in',
  'hotmail.com', 'outlook.com', 'live.com', 'msn.com',
  'aol.com', 'icloud.com', 'me.com', 'mac.com',
  'mail.com', 'zoho.com', 'protonmail.com', 'proton.me',
  'yandex.com', 'rediffmail.com', 'rediff.com',
  'gmx.com', 'gmx.net', 'fastmail.com', 'tutanota.com',
  'inbox.com', 'mail.ru',
];

const TRIM_ON_BLUR_FIELDS = ['company_name', 'admin_name', 'admin_email'];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { getToken } = useRecaptcha();
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [allowPersonalEmails, setAllowPersonalEmails] = useState(false);
  const [form, setForm] = useState({
    company_name: '', cin_number: '', company_type: 'NBFC',
    admin_name: '', admin_email: '', admin_password: '', admin_phone: '',
  });

  useEffect(() => {
    fetch(`${API_URL}/api/system/registration-settings`)
      .then(res => res.ok ? res.json() : { allow_personal_emails: false })
      .then(data => setAllowPersonalEmails(data.allow_personal_emails))
      .catch(() => setAllowPersonalEmails(false));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Only allow digits for phone field
    if (name === 'admin_phone') {
      const digits = value.replace(/\D/g, '');
      setForm(prev => ({ ...prev, [name]: digits }));
      return;
    }
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'admin_email') {
      const domain = value.trim().split('@')[1]?.toLowerCase();
      if (!allowPersonalEmails && domain && BLOCKED_DOMAINS.includes(domain)) {
        setEmailError(`Personal email domains like @${domain} are not allowed. Please use your work email.`);
      } else {
        setEmailError('');
      }
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (!TRIM_ON_BLUR_FIELDS.includes(name) || typeof value !== 'string') return;
    const trimmed = trimText(value);
    if (trimmed !== value) {
      setForm(prev => ({ ...prev, [name]: trimmed }));
    }
  };

  const CIN_REGEX = /^[LU]\d{5}[A-Z]{2}\d{4}[A-Z]{3}\d{6}$/;

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Trim non-password fields; password is never trimmed (whitespace is rejected)
    const cleanCompanyName = trimText(form.company_name);
    const cleanAdminName = trimText(form.admin_name);
    const cleanEmail = trimText(form.admin_email);
    const password = form.admin_password;

    setForm(prev => ({
      ...prev,
      company_name: cleanCompanyName,
      admin_name: cleanAdminName,
      admin_email: cleanEmail,
    }));

    if (!cleanCompanyName) {
      toast.error('This field cannot be empty or contain only spaces.');
      return;
    }
    if (cleanCompanyName.length < 2) {
      toast.error('Company Name must be at least 2 characters');
      return;
    }
    if (!cleanAdminName) {
      toast.error('This field cannot be empty or contain only spaces.');
      return;
    }
    if (cleanAdminName.length < 2) {
      toast.error('Full Name must be at least 2 characters');
      return;
    }
    if (!cleanEmail) {
      toast.error('Please enter a valid email address.');
      return;
    }
    const passwordError = validatePassword(password);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }
    if (!CIN_REGEX.test(form.cin_number)) {
      toast.error('Invalid CIN format. Expected: L/U + 5 digits + 2 letters + 4 digits + 3 letters + 6 digits (e.g., U65910MH2020PLC123456)');
      return;
    }
    if (emailError) {
      toast.error(emailError);
      return;
    }
    if (!/^\d{10}$/.test(form.admin_phone)) {
      toast.error('Phone number must be exactly 10 digits');
      return;
    }
    setLoading(true);
    try {
      const captcha_token = await getToken('register');
      const res = await fetch(`${API_URL}/api/auth/register-company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          company_name: cleanCompanyName,
          admin_name: cleanAdminName,
          admin_email: cleanEmail,
          admin_password: password,
          captcha_token,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        let message = 'Registration failed';
        if (typeof detail === 'string' && detail) {
          message = detail;
        } else if (Array.isArray(detail)) {
          message = detail.map(e => e.msg || JSON.stringify(e)).join('; ');
        }
        throw new Error(message);
      }
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('tenant', JSON.stringify({
        id: data.tenant.id,
        company_name: data.tenant.company_name,
        logo_url: data.tenant.logo_url,
        theme_id: data.tenant.theme_id,
      }));
      toast.success('Company registered successfully!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left — Pitch */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex-col justify-between p-12">
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>LMS</h1>
          <p className="text-slate-400 text-sm">Lending Management System</p>
        </div>
        <div className="space-y-8">
          <h2 className="text-4xl font-bold leading-tight" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Digitize your<br />lending operations
          </h2>
          <p className="text-slate-300 text-lg max-w-md">
            The complete SaaS platform for NBFCs, HFCs, and microfinance institutions. Launch in minutes, not months.
          </p>
          <div className="grid grid-cols-2 gap-6 mt-8">
            {FEATURES.map((f) => (
              <div key={f.title} className="space-y-2">
                <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <f.icon className="h-5 w-5 text-white/80" />
                </div>
                <h3 className="font-semibold text-sm">{f.title}</h3>
                <p className="text-xs text-slate-400">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-slate-500">Trusted by NBFCs across India</p>
      </div>

      {/* Right — Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md space-y-6">
          <div className="lg:hidden text-center mb-4">
            <h1 className="text-2xl font-bold">LMS</h1>
            <p className="text-sm text-muted-foreground">Register your company</p>
          </div>
          <div>
            <h2 className="text-2xl font-bold" data-testid="register-heading">Register your company</h2>
            <p className="text-sm text-muted-foreground mt-1">Get started with your lending platform in minutes</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label htmlFor="company_name">Company Name <span className="text-red-500">*</span> </Label>
                <Input id="company_name" name="company_name" value={form.company_name} onChange={handleChange} onBlur={handleBlur} placeholder="ABC Finance Ltd" required data-testid="register-company-name" />
              </div>
              <div>
                <Label htmlFor="cin_number">CIN Number <span className="text-red-500">*</span> </Label>
                <Input id="cin_number" name="cin_number" value={form.cin_number} onChange={handleChange} placeholder="L65910MH2017PLC..." required data-testid="register-cin-number" />
              </div>
              <div>
                <Label htmlFor="company_type">Company Type <span className="text-red-500">*</span> </Label>
                <select id="company_type" name="company_type" value={form.company_type} onChange={handleChange} className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm" data-testid="register-company-type">
                  <option value="NBFC">NBFC</option>
                  <option value="HFC">HFC</option>
                  <option value="MFI">MFI</option>
                  <option value="Bank">Bank</option>
                </select>
              </div>
            </div>
            <div className="border-t pt-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Admin Account</p>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="admin_name">Full Name <span className="text-red-500">*</span> </Label>
                  <Input id="admin_name" name="admin_name" value={form.admin_name} onChange={handleChange} onBlur={handleBlur} placeholder="Rajesh Kumar" required data-testid="register-admin-name" />
                </div>
                <div>
                  <Label htmlFor="admin_email">Work Email <span className="text-red-500">*</span> </Label>
                  <Input id="admin_email" name="admin_email" type="email" value={form.admin_email} onChange={handleChange} onBlur={handleBlur} placeholder="admin@yourcompany.com" required data-testid="register-admin-email" className={emailError ? 'border-red-500' : ''} />
                  {emailError && <p className="text-xs text-red-500 mt-1" data-testid="email-error">{emailError}</p>}
                  <p className="text-xs text-muted-foreground mt-1">Only business/work email addresses are accepted</p>
                </div>
                <div>
                  <Label htmlFor="admin_password">Password <span className="text-red-500">*</span> </Label>
                  <div className="relative">
                    <Input id="admin_password" name="admin_password" type={showPassword ? 'text' : 'password'} value={form.admin_password} onChange={handleChange} placeholder="e.g. Password@123" required minLength={8} data-testid="register-admin-password" className="pr-10" />
                    <button type="button" onClick={() => setShowPassword(prev => !prev)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Min 8 chars with upper, lower, number &amp; special character. No spaces.</p>
                </div>
                <div>
                  <Label htmlFor="admin_phone">Phone <span className="text-red-500">*</span></Label>
                  <Input
                    id="admin_phone"
                    name="admin_phone"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={form.admin_phone}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setForm(prev => ({ ...prev, admin_phone: val }));
                    }}
                    placeholder="9876543210"
                    required
                    pattern="\d{10}"
                    title="Enter a 10-digit phone number"
                    data-testid="register-admin-phone"
                  />
                  {form.admin_phone && form.admin_phone.length > 0 && form.admin_phone.length < 10 && (
                    <p className="text-xs text-red-500">Phone must be exactly 10 digits</p>
                  )}
                </div>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
              {loading ? 'Creating your account...' : 'Register Company'}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary hover:underline" data-testid="register-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
